import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

const SiteModeContext = createContext(null);

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export const SiteModeProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [siteMode, setSiteMode] = useState('dev');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState({ source: 'env_fallback', updatedAt: null, updatedByEmail: null });

  const fetchSiteMode = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/system-settings/site-mode`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || 'Unable to fetch site mode');
      setSiteMode(payload.site_mode || 'dev');
      setMeta({
        source: payload.source || 'env_fallback',
        updatedAt: payload.updated_at || null,
        updatedByEmail: payload.updated_by_email || null,
      });
    } catch (fetchError) {
      setError(fetchError.message || 'Unable to fetch site mode');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSiteMode();
  }, [token]);

  const updateSiteMode = async (nextMode) => {
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE}/api/system-settings/site-mode`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ site_mode: nextMode }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.detail || 'Unable to update site mode');

    setSiteMode(payload.site_mode || nextMode);
    setMeta({
      source: payload.source || 'database',
      updatedAt: payload.updated_at || null,
      updatedByEmail: payload.updated_by_email || user?.email || null,
    });

    return payload;
  };

  const value = useMemo(() => ({
    siteMode,
    loading,
    error,
    meta,
    refreshSiteMode: fetchSiteMode,
    updateSiteMode,
  }), [siteMode, loading, error, meta]);

  return <SiteModeContext.Provider value={value}>{children}</SiteModeContext.Provider>;
};

export const useSiteMode = () => {
  const context = useContext(SiteModeContext);
  if (!context) {
    throw new Error('useSiteMode must be used within SiteModeProvider');
  }
  return context;
};
